import {
  collection,
  doc,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  getFirestore,
  getCountFromServer,
  Transaction,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';

import {
  ref,
  listAll,
  getStorage,
} from 'firebase/storage';

import { getApp } from 'firebase/app';
import type { KonvaRack, Rack, Room, RoomObject, Slot, SlotAssignment, State } from './types';
import { errorMessage } from '../assets/js/helpers/FirebaseErrors';
import { planLimits as limits } from '../assets/js/helpers/BillingPlanHelper.ts';

function rackRef(context: State) {
  const fbApp = getApp(context.firebaseApp);
  const db = getFirestore(fbApp, context.dbid);

  if (context.team && context.userMode === 'team') {
    return collection(db, 'teams', context.team, 'racks');
  } else if (context.user) {
    return collection(db, 'users', context.user.uid, 'racks');
  } else {
    return null;
  }
}

function roomRef(context: State) {
  const fbApp = getApp(context.firebaseApp);
  const db = getFirestore(fbApp, context.dbid);

  if (context.team && context.userMode === 'team') {
    return collection(db, 'teams', context.team, 'rooms');
  } else if (context.user) {
    return collection(db, 'users', context.user.uid, 'rooms');
  } else {
    return null;
  }
}

async function getMaxRooms(subscription: string) {
  switch (subscription.toLowerCase()) {
    case 'team':
    case 'plus':
      return limits.plus.rooms();
    case 'basic':
      return limits.basic.rooms();
    default:
      return limits.free.rooms();
  }
}

export async function fetchRooms() {
  try {
    const roomData = {
      rooms: [] as Room[],
      count: 0,
    };
    const docRef = roomRef(this);

    if (!docRef) {
      return roomData;
    }

    const querySnapshot = await getDocs(docRef);

    // Use map to convert each roomDoc to a promise that resolves when all racks are processed
    const promises = querySnapshot.docs.map(async (roomDoc) => {
      const data = roomDoc.data() as Room;
      data.id = roomDoc.id;
      data.name = roomDoc.data().name;
      data.tags = roomDoc.data().tags;
      data.location = roomDoc.data().location;
      data.notes = roomDoc.data().notes;
      data.racks = roomDoc.data().racks;
      data.width = roomDoc.data().width;
      data.height = roomDoc.data().height;

      let slotsFull = 0;
      let roomSize = 0;
      let rackCount = 0;

      // Use Promise.all to wait for all promises to resolve before continuing
      await Promise.all(
        roomDoc.data().racks.map(async (rack: Rack) => {
          rackCount++;

          // Get size of rack and slots full from rack document
          const curRackRef = rackRef(this);

          if (!curRackRef) {
            return;
          }

          const curRack = doc(curRackRef, rack.id);
          const rackDocSnap = await getDoc(curRack);
          if (rackDocSnap.exists()) {
            const rackData = rackDocSnap.data();
            roomSize += rackData.size;

            // If rack is v2, add slotsFull from slotAssignments
            if (rackData.slotAssignments) {
              rackData.slotAssignments.forEach((slot: SlotAssignment) => {
                slotsFull += slot.halfWidth ? slot.size / 2 : slot.size;
              });
            } else {
              rackData.slots.forEach((slot: Slot) => {
                slotsFull += slot.halfWidth ? slot.size / 2 : slot.size;
              });
            }
          }
        }),
      );

      if (slotsFull == 0) {
        data.full = '0%';
      } else {
        data.full = Math.round((slotsFull / roomSize) * 100) + '%';
      }

      data.slotsFull = slotsFull;
      data.rackCount = rackCount;

      roomData.rooms.push(data);
      roomData.count++;
    });

    // Wait for all promises to resolve before returning roomData
    await Promise.all(promises);
    return roomData;
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function addRoom() {
  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to add rooms.');
  }

  try {
    const localRoomRef = roomRef(this);

    if (!localRoomRef) {
      throw new Error('Error retrieving room from database.');
    }

    const defaultRoom = {
      name: 'New Room',
      location: '',
      tags: [],
      racks: [],
      rackIds: [],
      objects: [],
      labels: [],
      notes: '',
      width: 1000,
      height: 1000,
      blockSize: 50,
      fontSize: 12,
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };

    const newDocId = doc(localRoomRef).id;

    const docRef = await this.runSubscriptionTransaction(async (sub: 'Free' | 'Basic' | 'Plus', tx: Transaction) => {
      const maxRooms = await getMaxRooms(sub);
      let numRooms = 0;

      try {
        const snapshot = await getCountFromServer(localRoomRef);
        numRooms = snapshot.data().count;
      } catch {
        // User has no racks
      }

      if (numRooms < maxRooms) {
        tx.set(doc(localRoomRef, newDocId), defaultRoom);
        return Promise.resolve(newDocId);
      } else {
        return Promise.resolve(false);
      }
    });

    if (docRef) {
      return docRef;
    } else {
      throw new Error('You have reached the maximum number of rooms.');
    }
  } catch (e: any) {
    console.error(e);
    if (e.code === 'permission-denied' || e.message.includes('maximum number of rooms')) {
      throw new Error('You have reached the maximum number of rooms.');
    } else {
      throw new Error(errorMessage(e));
    }
  }
}

export async function deleteRoom(id: string) {
  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to delete rooms.');
  }

  try {
    const localRoomRef = roomRef(this);

    if (!localRoomRef) {
      throw new Error('Error retrieving room from database.');
    }

    const curDoc = doc(localRoomRef, id);
    await deleteDoc(curDoc);
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function fetchRoom({ id }: { id: string }) {
  try {
    const localRoomRef = roomRef(this);

    if (!localRoomRef) {
      throw new Error('Error retrieving room from database.');
    }

    const querySnapshot = await getDoc(doc(localRoomRef, id));

    if (querySnapshot.exists()) {

      const data = querySnapshot.data() as Room;
      data.id = querySnapshot.id;

      const localRackRef = rackRef(this);

      if (!localRackRef) {
        throw new Error('Error retrieving rack from database.');
      }

      // Use map to convert each rack to a promise that resolves when all racks are processed
      const promises = querySnapshot.data().racks.map(async (rack: Rack) => {
        const rackId = rack.id;

        // Get rack document
        const curRackRef = doc(localRackRef, rackId);
        const rackDocSnap = await getDoc(curRackRef);

        if (data.racks) {
          if (rackDocSnap.exists()) {
            const rackData = rackDocSnap.data();

            // add rack name to corresponding object in data array
            data.racks.forEach((rack: KonvaRack) => {
              if (rack.id == rackId) {
                rack.name = rackData.name;
              }
            });
          } else {
            // if rack doesn't exist, remove it from the room
            data.racks = data.racks.filter(rack => rack.id !== rackId);
          }
        }
      });

      // Wait for all promises to resolve before returning roomData
      await Promise.all(promises);
      return data;
    } else {
      throw new Error('Document doesn\'t exist');
    }
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function updateRoom(room: Room) {
  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to update rooms.');
  }

  try {
    const rackIds: string[] = [];

    if (room.racks) {
      room.racks.forEach((rack: KonvaRack) => {
        if (rack.id) {
          rackIds.push(rack.id);
        }
      });
    }
    room.rackIds = rackIds;

    const localRoomRef = roomRef(this);

    if (!localRoomRef) {
      throw new Error('Error retrieving room from database.');
    }

    room.updatedAt = Timestamp.fromDate(new Date());
    const curDoc = doc(localRoomRef, room.id);
    await setDoc(curDoc, room);
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function duplicateRoom(id: string) {
  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to duplicate rooms.');
  }

  try {
    // Fetch the existing room
    const existingRoom = await this.fetchRoom({ id });

    if (!existingRoom) {
      throw new Error('Room not found.');
    }

    const localRoomRef = roomRef(this);

    if (!localRoomRef) {
      throw new Error('Error retrieving room from database.');
    }

    // Create a copy of the room data
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _unused, ...roomDataWithoutId } = existingRoom;
    const duplicatedRoom: Room = {
      ...roomDataWithoutId,
      name: 'Copy of ' + (existingRoom.name || 'New Room'),
      objects: existingRoom.objects ? existingRoom.objects.map((obj: RoomObject) => ({
        ...obj,
        id: crypto.randomUUID(),
      })) : [],
      labels: existingRoom.labels ? existingRoom.labels.map((label: RoomObject) => ({
        ...label,
        id: crypto.randomUUID(),
      })) : [],
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };

    const newDocId = doc(localRoomRef).id;

    const docRef = await this.runSubscriptionTransaction(async (sub: 'Free' | 'Basic' | 'Plus', tx: Transaction) => {
      const maxRooms = await getMaxRooms(sub);
      let numRooms = 0;

      try {
        const snapshot = await getCountFromServer(localRoomRef);
        numRooms = snapshot.data().count;
      } catch {
        // User has no rooms
      }

      if (numRooms < maxRooms) {
        tx.set(doc(localRoomRef, newDocId), duplicatedRoom);

        if (duplicatedRoom.racks) {
          // Assign racks to room
          duplicatedRoom.racks.forEach((rack: KonvaRack) => {
            this.assignRoomToRack(newDocId, duplicatedRoom.name, rack.id, tx);
          });
        }
        return Promise.resolve(newDocId);
      } else {
        return Promise.resolve(false);
      }
    });

    if (docRef) {
      return docRef;
    } else {
      throw new Error('You have reached the maximum number of rooms.');
    }
  } catch (e: any) {
    console.error(e);
    if (e.code === 'permission-denied' || e.message.includes('maximum number of rooms')) {
      throw new Error('You have reached the maximum number of rooms.');
    } else {
      throw new Error(errorMessage(e));
    }
  }
}

export async function getRoomCount() {
  try {
    const localRoomRef = roomRef(this);

    if (!localRoomRef) {
      return 0;
    }
    const snapshot = await getCountFromServer(localRoomRef);
    return snapshot.data().count;
  } catch (e: any) {
    console.warn(e);
    return 0;
  }
}

export async function fetchObjectTypes() {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  try {
    const listRef = ref(storage, 'global/images/objects');
    const types = [] as { label: string, title: string, value: string, key: number }[];
    let i = 1;

    await listAll(listRef)
      .then((res) => {
        res.items.forEach(async (itemRef) => {
          // Types are in the format of "name.1U.png"
          types.push({
            // Get everything before the first period
            label: itemRef.name.substring(0, itemRef.name.indexOf('.')),
            title: itemRef.name.substring(0, itemRef.name.indexOf('.')),
            value: itemRef.name,
            key: i,
          });
          i++;
        });
      })
      .catch((error) => {
        throw new Error(error);
      });
    return types;
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function assignRoomToRack(roomId: string, roomName: string, rackId: string, tx?: Transaction) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  let racksBasePath: [string, string, string] | [string, string, string, string, string];

  if (this.team && this.userMode === 'team') {
    racksBasePath = ['teams', this.team, 'racks'];
  } else {
    racksBasePath = ['users', this.user.uid, 'racks'];
  }

  try {
    // Add assignment to room
    const rackRef = doc(db, ...racksBasePath, rackId);
    const rackDoc = await getDoc(rackRef);

    if (!rackDoc.exists()) {
      throw new Error('Rack not found');
    }

    const rackData = rackDoc.data();
    const updatedAssignments = [...rackData.rooms, {
      id: roomId,
      name: roomName,
    }];

    if (tx) {
      tx.update(rackRef, {
        rooms: updatedAssignments,
        updatedAt: new Date(),
      });
    } else {
      await updateDoc(rackRef, {
        rooms: updatedAssignments,
        updatedAt: new Date(),
      });
    }
  } catch (error) {
    console.error('Error assigning room to rack:', error);
    throw error;
  }
}
